import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { RouteInfo, RouteInfoStop } from '../entities/RouteInfo.js';
import type { AvailableFieldEngineer } from '../entities/RouteOptimizationResult.js';
import { extractIDOnlyFromFEKey } from './extractIDOnlyFromFEKey.js';
import { getAreaFromVehicleId } from './getAreaFromVehicleId.js';
import { isInterventionStop } from './isInterventionStop.js';

interface AreaStats {
  area: string;
  feCount: number;
  ticketCount: number;
  hesTicketCount: number;
  clientTicketCount: number;
  entTicketCount: number;
  imacTicketCount: number;
  unknownTicketTypeCount: number;
  depotCount: number;
  totalTravelMinutes: number;
  totalTravelMeters: number;
  totalServiceMinutes: number;
}

interface GlobalStats {
  totalFEs: number;
  totalAvailableFEs: number;
  totalUnavailableFEsWithRoutes: number;
  totalAvailableInternalFEs: number;
  totalAvailablePartnerFEs: number;
  totalAssignedInternalFEs: number;
  totalAssignedPartnerFEs: number;
  totalTickets: number;
  totalHesTickets: number;
  totalClientTickets: number;
  totalEntTickets: number;
  totalImacTickets: number;
  totalUnknownTicketTypeTickets: number;
  totalDepots: number;
  totalTravelMinutes: number;
  totalTravelMeters: number;
  totalServiceMinutes: number;
  totalFesWithRoutes: number;
  areaStats: AreaStats[];
}

export type CalculateStatsPlanning = {
  routes: Maybe<
    Array<
      Pick<EntityData<RouteInfo>, 'vehicleId'> & {
        stops?: Maybe<
          Array<
            Pick<
              EntityData<RouteInfoStop>,
              | 'type'
              | 'ticketType'
              | 'lat'
              | 'lng'
              | 'travelDurationMinutes'
              | 'travelMeters'
              | 'serviceDurationMinutes'
              | 'ticketId'
            >
          >
        >;
      }
    >
  >;
} & {
  fieldEngineersAvailable: Maybe<Array<Pick<EntityData<AvailableFieldEngineer>, 'id'>>>;
} & {
  partnerFieldEngineersAvailable?: Maybe<Array<Pick<EntityData<AvailableFieldEngineer>, 'id'>>>;
};

export function calculateStats(planning: CalculateStatsPlanning): GlobalStats {
  const areaMap = new globalThis.Map<string, AreaStats>();
  const routes = planning.routes;

  for (const route of routes ?? []) {
    const area = getAreaFromVehicleId(route.vehicleId);
    if (!areaMap.has(area)) {
      areaMap.set(area, {
        area,
        feCount: 0,
        ticketCount: 0,
        hesTicketCount: 0,
        clientTicketCount: 0,
        entTicketCount: 0,
        imacTicketCount: 0,
        unknownTicketTypeCount: 0,
        depotCount: 0,
        totalTravelMinutes: 0,
        totalTravelMeters: 0,
        totalServiceMinutes: 0,
      });
    }
    const stats = areaMap.get(area)!;
    stats.feCount += 1;

    if (route.stops) {
      const uniqueDepots = new Set<string>();
      for (const stop of route.stops) {
        if (stop.type === 'ticket' && !isInterventionStop(stop)) {
          stats.ticketCount += 1;
          if (stop.ticketType === 'HES') stats.hesTicketCount += 1;
          else if (stop.ticketType === 'Client') stats.clientTicketCount += 1;
          else if (stop.ticketType === 'ENT' || stop.ticketType === 'Enterprise') stats.entTicketCount += 1;
          else if (stop.ticketType === 'IMAC') stats.imacTicketCount += 1;
          else stats.unknownTicketTypeCount += 1;
        }
        if (stop.type === 'depot' && stop.lat && stop.lng) {
          uniqueDepots.add(`${stop.lat},${stop.lng}`);
        }
        stats.totalTravelMinutes += stop.travelDurationMinutes ?? 0;
        stats.totalTravelMeters += stop.travelMeters ?? 0;
        stats.totalServiceMinutes += stop.serviceDurationMinutes ?? 0;
      }
      stats.depotCount += uniqueDepots.size;
    }
  }

  const areaStats = Array.from(areaMap.values()).sort((a, b) => a.area.localeCompare(b.area));
  const availableFieldEngineerIDs =
    planning.fieldEngineersAvailable?.map(engineer => extractIDOnlyFromFEKey(engineer.id)) ?? [];
  const availablePartnerFieldEngineerIDs =
    planning.partnerFieldEngineersAvailable?.map(engineer => extractIDOnlyFromFEKey(engineer.id)) ?? [];
  const allAvailableIDs = [...availableFieldEngineerIDs, ...availablePartnerFieldEngineerIDs];

  // FEs that are not available but have routes due to already assigned tickets (especially interesting in the rerun and checksum runs)
  const unavailableEngineersWithRoutes = (planning.routes ?? [])
    .map(route => extractIDOnlyFromFEKey(route.vehicleId))
    .filter(vehicleId => vehicleId && !allAvailableIDs.includes(vehicleId));

  const routeVehicleIds = (planning.routes ?? [])
    .map(route => extractIDOnlyFromFEKey(route.vehicleId))
    .filter(Boolean) as string[];
  const assignedInternalFEs = routeVehicleIds.filter(id => availableFieldEngineerIDs.includes(id)).length;
  const assignedPartnerFEs = routeVehicleIds.filter(id => availablePartnerFieldEngineerIDs.includes(id)).length;

  return {
    totalFEs: allAvailableIDs.length + unavailableEngineersWithRoutes.length,
    totalAvailableFEs: availableFieldEngineerIDs.length + availablePartnerFieldEngineerIDs.length,
    totalUnavailableFEsWithRoutes: unavailableEngineersWithRoutes.length,
    totalAvailableInternalFEs: availableFieldEngineerIDs.length,
    totalAvailablePartnerFEs: availablePartnerFieldEngineerIDs.length,
    totalAssignedInternalFEs: assignedInternalFEs,
    totalAssignedPartnerFEs: assignedPartnerFEs,
    totalFesWithRoutes: (planning.routes ?? []).length,
    totalTickets: areaStats.reduce((sum, s) => sum + s.ticketCount, 0),
    totalHesTickets: areaStats.reduce((sum, s) => sum + s.hesTicketCount, 0),
    totalClientTickets: areaStats.reduce((sum, s) => sum + s.clientTicketCount, 0),
    totalEntTickets: areaStats.reduce((sum, s) => sum + s.entTicketCount, 0),
    totalImacTickets: areaStats.reduce((sum, s) => sum + s.imacTicketCount, 0),
    totalUnknownTicketTypeTickets: areaStats.reduce((sum, s) => sum + s.unknownTicketTypeCount, 0),
    totalDepots: areaStats.reduce((sum, s) => sum + s.depotCount, 0),
    totalTravelMinutes: areaStats.reduce((sum, s) => sum + s.totalTravelMinutes, 0),
    totalTravelMeters: areaStats.reduce((sum, s) => sum + s.totalTravelMeters, 0),
    totalServiceMinutes: areaStats.reduce((sum, s) => sum + s.totalServiceMinutes, 0),
    areaStats,
  };
}
